import Button from "components/Button/Button";
import { GroupSection } from "components/GroupSection";
import { Input } from "components/inputs/Input/Input";
import { MultiSelect } from "components/inputs/Select/MultiSelect";
import { SearchableSelect } from "components/inputs/Select/SearchableSelect";
import { Toggle } from "components/inputs/Toggle/Toggle";
import { useRoleStore } from "contexts/RootStore.context";
import { Content, Horizontal, StretchSpacer, Vertical } from "gls/lib";
import useDevice from "hooks/useDevice";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { configRoutePath } from "router/routePaths";
import { formStyle } from "styling/App.style";
import { style } from "typestyle";
import { RequiredValidationError } from "utils/formstate";
import { WorkgroupPermissions, AssetPermissions, PeoplePermissions, SecurityPermissions, PermissionList, DevicePermissions } from "../RoleConfigDto";
import FormHeader from "components/FormHeader";

type RoleFormProps = {
  roleId?: string;
  canManageRoles: boolean;
  saveHandler: () => void;
}

export const RoleForm = observer((props: RoleFormProps) => {

  const {
    workgroups,
    roleForm,
    saveLoading,
    isAnyChange,
    allAssetTypes,
    allOwners,
  } = useRoleStore();

  const { dimension } = useDevice();
  const navigate = useNavigate();
  const securityPermissionObj = new PermissionList<SecurityPermissions>(roleForm.$.securityPermissions.value)
  const AssetPermissionObj = new PermissionList<AssetPermissions>(roleForm.$.assetPermissions.value)
  const PeoplePermissionObj = new PermissionList<PeoplePermissions>(roleForm.$.peoplePermissions.value)
  const DevicePermissionObj = new PermissionList<DevicePermissions>(roleForm.$.devicePermissions.value)

  return (
    <Vertical
      spacing={20}
      style={{
        height: dimension.height - 90,
        overflowY: "auto",
        paddingTop: 34,
        paddingRight: 4,
      }}>

      <FormHeader text="ROLE INFORMATION" bold />
      <Horizontal spacing={40} className={formStyle} padding={30}>
        <SearchableSelect
          label="Workgroup Name:"
          className={fieldStyle}
          required
          disabled={!!props.roleId || !props.canManageRoles}
          key="workgroup-select"
          id="workgroup-select"
          name="workgroup-select"
          options={workgroups}
          onChange={(value) => {
            roleForm.$.workgroupId.onChange(value)
            roleForm.$.PeopleWorkgroups.onChange([])
          }}
          value={roleForm.$.workgroupId.value}
        />
        <Input
          label="Role Name:"
          required
          className={fieldStyle}
          value={roleForm.$.name.value}
          onChange={(value) => { roleForm.$.name.onChange(value) }}
          disabled={!props.canManageRoles}
          key="role-name-input"
          id="role-name-input"
          name="role-name-input"
          dataTestId="role-name-input"
          maxLength={30}
          error={roleForm.$.name.error !== RequiredValidationError ? roleForm.$.name.error : ""}
        />
      </Horizontal>

      <Vertical padding={30} spacing={20} className={formStyle}>
        <FormHeader text="SECURITY PERMISSIONS" bold />
        <GroupSection title="Access">
          <Toggle
            key="login_mobile_app-permission"
            id="login_mobile_app-permission"
            name="login_mobile_app-permission"
            disabled={!props.canManageRoles}
            value={securityPermissionObj.includes(SecurityPermissions.access_mobile_app)}
            onChange={(value) => {
              if (value) {
                securityPermissionObj.add(SecurityPermissions.access_mobile_app)
              }
              else {
                securityPermissionObj.remove(SecurityPermissions.access_mobile_app)
                securityPermissionObj.remove(SecurityPermissions.locate_this_person)
              }
              roleForm.$.securityPermissions.onChange(securityPermissionObj.permissions);
            }}
            label="Mobile App"
            labelPlace="right"
          />
          <Toggle
            key="login_portal_app-permission"
            id="login_portal_app-permission"
            name="login_portal_app-permission"
            disabled={!props.canManageRoles}
            value={securityPermissionObj.includes(SecurityPermissions.access_portal)}
            onChange={(value) => {
              if (value) {
                securityPermissionObj.add(SecurityPermissions.access_portal)
              }
              else {
                securityPermissionObj.remove(SecurityPermissions.access_portal)
              }
              roleForm.$.securityPermissions.onChange(securityPermissionObj.permissions);
            }}
            label="Portal"
            labelPlace="right"
          />
          <Toggle
            key="locate_person-permission"
            id="locate_person-permission"
            name="locate_person-permission"
            disabled={!props.canManageRoles}
            value={securityPermissionObj.includes(SecurityPermissions.locate_this_person)}
            onChange={(value) => {
              if (value) {
                securityPermissionObj.add(SecurityPermissions.locate_this_person)
                securityPermissionObj.add(SecurityPermissions.access_mobile_app)
              }
              else {
                securityPermissionObj.remove(SecurityPermissions.locate_this_person)
              }
              roleForm.$.securityPermissions.onChange(securityPermissionObj.permissions);
            }}
            label="Locate this person"
            labelPlace="right"
          />
        </GroupSection>
      </Vertical>

      <Vertical padding={30} spacing={20} className={formStyle}>
        <FormHeader text="ASSETS PERMISSIONS" bold />
        <GroupSection title="Asset Permissions">
          <Toggle
            key="view-assetpermission"
            id="view-assetpermission"
            name="view-assetpermission"
            disabled={!props.canManageRoles}
            value={AssetPermissionObj.includes(AssetPermissions.view)}
            onChange={(value) => {
              if (value) {
                AssetPermissionObj.add(AssetPermissions.view)
              }
              else {
                AssetPermissionObj.remove(AssetPermissions.view)
                AssetPermissionObj.remove(AssetPermissions.manage)
              }
              roleForm.$.assetPermissions.onChange(AssetPermissionObj.permissions);
            }}
            label="View"
            labelPlace="right"
          />
          <Toggle
            key="create-assetpermission"
            id="create-assetpermission"
            name="create-assetpermission"
            disabled={!props.canManageRoles}
            value={AssetPermissionObj.includes(AssetPermissions.manage)}
            onChange={(value) => {
              if (value) {
                AssetPermissionObj.add(AssetPermissions.manage)
                AssetPermissionObj.add(AssetPermissions.view)
              }
              else {
                AssetPermissionObj.remove(AssetPermissions.manage)
              }
              roleForm.$.assetPermissions.onChange(AssetPermissionObj.permissions);
            }}
            label="Create and Manage"
            labelPlace="right"
          />
          <Toggle
            key="report-assetpermission"
            id="report-assetpermission"
            name="report-assetpermission"
            disabled={!props.canManageRoles}
            value={AssetPermissionObj.includes(AssetPermissions.reports)}
            onChange={(value) => {
              if (value) {
                AssetPermissionObj.add(AssetPermissions.reports)
                AssetPermissionObj.add(AssetPermissions.view)
              }
              else {
                AssetPermissionObj.remove(AssetPermissions.reports)
              }
              roleForm.$.assetPermissions.onChange(AssetPermissionObj.permissions);
            }}
            label="Reports/Logs"
            labelPlace="right"
          />
        </GroupSection>
        <MultiSelect
          label="Asset Types"
          id="asset_types_select"
          options={allAssetTypes}
          value={roleForm.$.assetTypes.value || []}
          onChange={roleForm.$.assetTypes.onChange}
          className={multiSelectStyle}
        />
        <MultiSelect
          label="Owners"
          id="asset_owners_select"
          options={allOwners}
          value={roleForm.$.owners.value || []}
          onChange={roleForm.$.owners.onChange}
          className={multiSelectStyle}
        />
      </Vertical >

      <Vertical padding={30} spacing={20} className={formStyle}>
        <FormHeader text="PEOPLE PERMISSIONS" bold />
        <MultiSelect
          id="people-workgroup-select"
          label="Workgroups"
          options={workgroups}
          className={multiSelectStyle}
          value={roleForm.$.PeopleWorkgroups.value}
          onChange={(values) => {
            roleForm.$.PeopleWorkgroups.onChange(values)
            if (values.length > 0) {
              PeoplePermissionObj.add(PeoplePermissions.view)
              roleForm.$.peoplePermissions.onChange(PeoplePermissionObj.permissions);
            }
          }} />
        <GroupSection title="People Permissions">
          <Toggle
            key="view-peoplepermission"
            id="view-peoplepermission"
            name="view-peoplepermission"
            disabled={!props.canManageRoles}
            value={PeoplePermissionObj.includes(PeoplePermissions.view)}
            onChange={(value) => {
              if (value) {
                PeoplePermissionObj.add(PeoplePermissions.view)
              }
              else {
                PeoplePermissionObj.remove(PeoplePermissions.view)
              }
              roleForm.$.peoplePermissions.onChange(PeoplePermissionObj.permissions);
              roleForm.validate()
            }}
            label="View"
            labelPlace="right"
          />
          <Toggle
            key="report-peoplepermission"
            id="report-peoplepermission"
            name="report-peoplepermission"
            disabled={!props.canManageRoles}
            value={PeoplePermissionObj.includes(PeoplePermissions.reports)}
            onChange={(value) => {
              if (value) {
                PeoplePermissionObj.add(PeoplePermissions.reports)
              }
              else {
                PeoplePermissionObj.remove(PeoplePermissions.reports)
              }
              roleForm.$.peoplePermissions.onChange(PeoplePermissionObj.permissions);
              roleForm.validate()
            }}
            label="Reports/Logs"
            labelPlace="right"
          />
        </GroupSection>
      </Vertical>

      <Vertical padding={30} spacing={20} className={formStyle}>
        <FormHeader text="DEVICE PERMISSIONS" bold />
        <GroupSection title="Device Permissions">
          <Toggle
            key="view-device-permission"
            id="view-device-permission"
            name="view-device-permission"
            disabled={!props.canManageRoles}
            value={DevicePermissionObj.includes(DevicePermissions.view)}
            onChange={(value) => {
              if (value) {
                DevicePermissionObj.add(DevicePermissions.view)
              }
              else {
                DevicePermissionObj.remove(DevicePermissions.view)
              }
              roleForm.$.devicePermissions.onChange(DevicePermissionObj.permissions);
              roleForm.validate()
            }}
            label="View"
            labelPlace="right"
          />
          <Toggle
            key="report-device-permission"
            id="report-device-permission"
            name="report-device-permission"
            disabled={!props.canManageRoles}
            value={DevicePermissionObj.includes(DevicePermissions.reports)}
            onChange={(value) => {
              if (value) {
                DevicePermissionObj.add(DevicePermissions.reports)
              }
              else {
                DevicePermissionObj.remove(DevicePermissions.reports)
              }
              roleForm.$.devicePermissions.onChange(DevicePermissionObj.permissions);
              roleForm.validate()
            }}
            label="Reports/Logs"
            labelPlace="right"
          />
        </GroupSection>
      </Vertical>

      <Vertical padding={30} spacing={20} className={formStyle}>
        <FormHeader text="WORKGROUP ADMIN PERMISSIONS" bold />
        <GroupSection title="Roles">
          <Toggle
            key="create-adminpermission"
            id="create-adminpermission"
            name="create-adminpermission"
            disabled={!props.canManageRoles}
            value={!!roleForm.$.adminPermissions.value.find((item: string) => item === WorkgroupPermissions.create_and_manage_roles)}
            onChange={(value) => {
              if (value) {
                roleForm.$.adminPermissions.value.push(WorkgroupPermissions.create_and_manage_roles)
              }
              else {
                const newlist = roleForm.$.adminPermissions.value.filter(item => item !== WorkgroupPermissions.create_and_manage_roles)
                roleForm.$.adminPermissions.onChange(newlist);
              }
            }}
            label="Create and Manage Roles"
            labelPlace="right"
          />
          <Toggle
            key="view-adminpermission"
            id="view-adminpermission"
            name="view-adminpermission"
            disabled={!props.canManageRoles}
            value={!!roleForm.$.adminPermissions.value.find((item: string) => item === WorkgroupPermissions.view_roles)}
            onChange={(value) => {
              if (value) {
                roleForm.$.adminPermissions.value.push(WorkgroupPermissions.view_roles)
              }
              else {
                const newlist = roleForm.$.adminPermissions.value.filter(item => item !== WorkgroupPermissions.view_roles)
                roleForm.$.adminPermissions.onChange(newlist);
              }
            }}
            label="View Roles"
            labelPlace="right"
          />
        </GroupSection>
      </Vertical>

      {
        props.canManageRoles && (
          <Content padding={[4, 0, 0, 0]} horizontalAlign="right">
            <Horizontal>
              {props.roleId &&
                <Button
                  outline
                  onClick={() => { navigate(configRoutePath.listRoles) }}
                  disabled={saveLoading}
                >
                  Cancel
                </Button>
              }
              <Button
                onClick={props.saveHandler}
                disabled={!!(roleForm.hasError || (props.roleId && !isAnyChange))}
                isLoading={saveLoading}
                id="save-button"
              >
                {props.roleId ? "Save" : "Create"}
              </Button>

            </Horizontal>
          </Content>
        )
      }
      <StretchSpacer className={style({ paddingBottom: 24 })} />
    </Vertical >
  );
});


const fieldStyle = style({
  maxWidth: "350px",
  minWidth: "350px",
});

const multiSelectStyle = style({
  maxWidth: "100% !important",
})